import React, { useMemo, useState } from "react";
import { Alert, Button, Card, Modal, OverlayTrigger, Spinner, Tooltip } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowUpRightFromSquare } from "@fortawesome/free-solid-svg-icons";
import { participantRecordSummary } from "../utils/participantRecords.js";
import { EventVacancyReport, getEventSubscriptionCapacity } from "./EventVacancyReport.js";

const numberLabel = (value) => value === null ? "—" : Number(value || 0).toLocaleString("pt-BR");

const SummaryRowAction = ({ label, onClick, icon = faArrowUpRightFromSquare, buttonText }) => {
  const action = (
    <span className="d-inline-block flex-shrink-0">
      <button type="button" className="btn btn-sm btn-primary" aria-label={label} onClick={onClick}>
        {buttonText || <FontAwesomeIcon icon={icon} />}
      </button>
    </span>
  );

  return buttonText ? action : (
    <OverlayTrigger placement="top" overlay={<Tooltip>{label}</Tooltip>}>
      {action}
    </OverlayTrigger>
  );
};

export default function ParticipantRecordsSummary({ records, dataTable, definition, loading, error, checkinsAvailable, onRetry, onViewParticipants, onViewRsvpTable }) {
  const [showRegistrationManagement, setShowRegistrationManagement] = useState(false);
  const summary = useMemo(() => participantRecordSummary(records, checkinsAvailable), [checkinsAvailable, records]);
  const capacity = getEventSubscriptionCapacity(dataTable);
  const rsvpAvailable = Boolean(definition && !loading && !error);
  const rsvpTooltip = loading ? "Carregando o RSVP principal de confirmação de participação."
    : error ? "Não foi possível identificar o RSVP principal de confirmação de participação."
      : definition ? `RSVP principal de confirmação: ${definition.name}. Registra a intenção de participar, não a entrada no evento.`
        : "Nenhum RSVP foi definido como confirmação principal de participação.";
  const notConfirmed = rsvpAvailable ? summary.total - summary.yes : null;
  const withoutCheckin = checkinsAvailable ? summary.total - summary.checkedIn : null;
  const hasCapacity = capacity.enabled && capacity.limit > 0;

  return <section className="mb-4" aria-labelledby="participant-records-summary-title">
    <div className="textHeaderAplicativo mb-2 d-flex justify-content-between align-items-center gap-2 flex-wrap">
      <h5 id="participant-records-summary-title" className="card-title mb-0">Resumo do evento</h5>
    </div>
    {error && <Alert variant="warning" className="p-2 d-flex justify-content-between align-items-center gap-2 flex-wrap"><span>{error} Os dados de cadastro e check-in continuam disponíveis.</span><Button size="sm" variant="outline-secondary" onClick={onRetry}>Tentar novamente</Button></Alert>}
    {!checkinsAvailable && <Alert variant="warning" className="p-2">Os dados de check-in não foram carregados. Isso não significa que não existam registros.</Alert>}
    <Card className="event-records-summary-panel mb-0 mt-3">
      <Card.Body className="p-0">
          <div className="event-records-summary-row">
            <div className="event-records-summary-metric">
              <h6 className="event-records-summary-heading mb-0">1. Participantes</h6>
            </div>
            <div className="event-records-summary-breakdown">
              <span className="event-records-summary-status"><strong>{numberLabel(summary.total)}</strong> registrados</span>
              <span className="event-records-summary-status" title={hasCapacity ? `${numberLabel(capacity.used)} inscrições utilizadas · ${numberLabel(capacity.remaining)} restantes · ${capacity.status.label}` : undefined}>
                {hasCapacity ? <>Limite: <strong>{numberLabel(capacity.limit)}</strong></> : capacity.enabled ? "Limite não configurado" : "Sem limite"}
              </span>
            </div>
            <SummaryRowAction label="Ver mais" buttonText="Ver mais" onClick={() => setShowRegistrationManagement(true)} />
          </div>
          <div className="event-records-summary-row">
            <div className="event-records-summary-metric">
              <h6 className="event-records-summary-heading mb-0">2. Registros</h6>
            </div>
            <div className="event-records-summary-breakdown">
              <span className="event-records-summary-status event-records-summary-status-active"><strong>{numberLabel(summary.active)}</strong> ativos</span>
              <span className="event-records-summary-status event-records-summary-status-danger"><strong>{numberLabel(summary.inactive)}</strong> inativos</span>
            </div>
            <SummaryRowAction label="Ver tabela de participantes" onClick={onViewParticipants} />
          </div>
          <div className="event-records-summary-row">
            <div className="event-records-summary-metric" title={rsvpTooltip}>
              <h6 className="event-records-summary-heading mb-0">3. Confirmação de presença</h6>
            </div>
            <div className="event-records-summary-breakdown">
              {rsvpAvailable ? <>
                <span className="event-records-summary-status event-records-summary-status-active"><strong>{numberLabel(summary.yes)}</strong> confirmados</span>
                <span className="event-records-summary-status event-records-summary-status-danger" title={`${numberLabel(summary.no)} não confirmaram · ${numberLabel(summary.unanswered)} sem resposta · ${numberLabel(summary.other)} para revisão.`}><strong>{numberLabel(notConfirmed)}</strong> não confirmados</span>
              </>
                : loading ? <span className="event-records-summary-status" role="status"><Spinner animation="border" size="sm" className="me-1" />Carregando…</span>
                  : <span className="event-records-summary-status">{error ? "RSVP indisponível" : "Sem RSVP principal"}</span>}
            </div>
            <SummaryRowAction label="Ver tabela de RSVPs: confirmação de presença" onClick={onViewRsvpTable} />
          </div>
          <div className="event-records-summary-row">
            <div className="event-records-summary-metric" title="Participantes únicos: presentes possuem ao menos um check-in; ausentes não possuem check-in registrado.">
              <h6 className="event-records-summary-heading mb-0">4. Check-ins</h6>
            </div>
            <div className="event-records-summary-breakdown">
              {checkinsAvailable ? <>
                <span className="event-records-summary-status event-records-summary-status-active"><strong>{numberLabel(summary.checkedIn)}</strong> presentes</span>
                <span className="event-records-summary-status event-records-summary-status-danger"><strong>{numberLabel(withoutCheckin)}</strong> ausentes</span>
              </> : <span className="event-records-summary-status">Dados indisponíveis</span>}
            </div>
            <SummaryRowAction label="Ver tabela de RSVPs: check-ins" onClick={onViewRsvpTable} />
          </div>
      </Card.Body>
    </Card>
    <Modal show={showRegistrationManagement} onHide={() => setShowRegistrationManagement(false)} size="lg" centered scrollable fullscreen="sm-down">
      <Modal.Header closeButton><Modal.Title>Gestão das inscrições</Modal.Title></Modal.Header>
      <Modal.Body><EventVacancyReport dataTable={dataTable} showHeader={false} alwaysShow /></Modal.Body>
      <Modal.Footer><Button variant="outline-secondary" onClick={() => setShowRegistrationManagement(false)}>Fechar</Button></Modal.Footer>
    </Modal>
  </section>;
}
